/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Prompt, useLocation, Redirect } from 'react-router-dom';

import { Skeleton } from '@material-ui/lab';
import { Location } from 'history';

import ConfirmationAlert from '~/components/Alert/ConfirmationAlert';
import FormCamunda from '~/components/Formik/FormCamunda';
import BackButton from '~/components/Header/BackButton';
import { useAuth } from '~/hooks/auth';
import { useSubmitModal } from '~/hooks/SubmitModal';
import history from '~/services/history';
import { IState } from '~/store';
import { getDynamicTableRequest } from '~/store/modules/dynamicTable/actions';
import { submitModalReset } from '~/store/modules/modal/actions';
import { StatusOption } from '~/store/modules/modal/types';
import {
  getProcessFormRequest,
  getProcessRequest,
  instantiateProcessRequest,
} from '~/store/modules/processes/actions';

import {
  Container,
  Content,
  FormContent,
  Title,
  RequiredFieldsText,
} from './styles';

const NewProcessForm: React.FC = () => {
  const [canNavigate, setCanNavigate] = useState<boolean>(false);
  const [cancelModal, setCancelModal] = useState<boolean>(false);
  const [location, setLocation] = useState<Location | null>(useLocation());

  const {
    user: { orgId },
  } = useAuth();

  const search = new URLSearchParams(useLocation().search);
  const tenantId = search.get('tenantId');
  const key = search.get('key');

  const loading = useSelector((state: IState) => state.processes.loading);
  const process = useSelector((state: IState) => state.processes.process);
  const submitStatus = useSelector((state: IState) => state.modal.submitStatus);

  const { handler } = useSubmitModal();

  const dispatch = useDispatch();

  useEffect(() => {
    if (process) {
      dispatch(getProcessFormRequest(process));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process?.id]);

  useEffect(() => {
    dispatch(submitModalReset());
    if (key && tenantId) {
      dispatch(getProcessRequest(key, tenantId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = {};

    process?.form?.forEach((process: any) => {
      if (process.props.id === 'dynamicTable') {
        process.properties.property.forEach((p: any) => {
          if (p.props.id === 'dynamicTableName') {
            Object.assign(params, {
              ...params,
              name: String(p.props.value),
              orgId,
            });

            dispatch(getDynamicTableRequest(params));
          }
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, process?.form]);

  useEffect(() => {
    if (submitStatus === StatusOption.SUCCESS) {
      setCanNavigate(true);
    }
  }, [submitStatus]);

  const handleCancel = (newLocation: Location) => {
    if (canNavigate) {
      return true;
    }

    setCancelModal(true);
    setLocation(newLocation);
    return false;
  };

  const hasValidTenantId = (): boolean => {
    return Number(tenantId) === Number(orgId);
  };

  return hasValidTenantId() ? (
    <>
      <BackButton to="/processes" />

      <Container>
        <Title>
          <h2>
            {!loading ? (
              process?.name
            ) : (
              <Skeleton
                variant="text"
                animation="wave"
                width="200px"
                height="20px"
                style={{ margin: '10px 0 5px 0' }}
              />
            )}
          </h2>
          <RequiredFieldsText>* Campos obrigatórios</RequiredFieldsText>
        </Title>

        <Content>
          <FormContent>
            {!loading && process?.form ? (
              <FormCamunda
                process={process}
                onDirty={(isDirty: boolean) => {
                  setCanNavigate(!isDirty);
                }}
                onSubmit={(variables, processFiles) =>
                  handler(() =>
                    dispatch(
                      instantiateProcessRequest(
                        process,
                        variables,
                        processFiles,
                      ),
                    ),
                  )
                }
              />
            ) : (
              <>
                {[...Array(4)].map(() => (
                  <>
                    <Skeleton
                      variant="text"
                      animation="wave"
                      width="160px"
                      height="20px"
                      style={{ margin: '8px 0 4px 0' }}
                    />
                    <Skeleton
                      variant="rect"
                      animation="wave"
                      height="42px"
                      style={{ borderRadius: '4px' }}
                    />
                  </>
                ))}
                <Skeleton
                  variant="rect"
                  animation="wave"
                  height="42px"
                  style={{ marginTop: '59px', borderRadius: '5px' }}
                />
              </>
            )}
          </FormContent>
        </Content>
      </Container>

      <Prompt
        when={!canNavigate}
        message={(location) => handleCancel(location)}
      />

      <ConfirmationAlert
        onOk={() => {
          setCanNavigate(true);
          setCancelModal(false);
          if (location) history.push(location);
        }}
        onCancel={() => {
          setCancelModal(false);
        }}
        open={cancelModal}
        modalState={(opened: boolean) => setCancelModal(opened)}
      />
    </>
  ) : (
    <Redirect to="/processes" />
  );
};

export default NewProcessForm;
